import React from 'react';
import {
  applyConfigurationRules,
  getConfigurationRulesForPage,
  refIsEqual,
  View3DConfigurationRule,
} from './model3dState';
import { getPageContent, useAppState } from '..';
import { Euler, Vector2, Vector3 } from 'three';
import { isContentModel } from '@gkn-ili/views/dist/PageContent';
import { isPageReferenceActivity } from '@gkn-ili/views/dist/OrderDocumentation';
import { ActivityView } from '@gkn-ili/views/dist/Activities';

export type View3DRepState = {
  visible: boolean;
  actionState: 'active' | 'animated' | 'none';
  offsetPosition: [number, number, number];
  color: number;

  ref3DObject?: { center: Vector3 };
};
export type View3DCameraState = { position: Vector3; rotation: Euler; target: Vector3 };

export type View3DCamera = {
  goHome: () => void;
  zoomFit: () => void;
  getState: () => View3DCameraState;
  get2DCoordinates: (position: Vector3) => Vector2;
};

const annotationColors = [0xff0000, 0x00ff00, 0x0000ff];

export type Annotation3D = ActivityView['annotations'][0] & {
  ref3DObject: { center: Vector3 }[];
  annotationRef: React.RefObject<HTMLDivElement>;
  color: number;
};

const cameraRef = React.createRef<View3DCamera>();

const context = React.createContext<{
  configurationRules: View3DConfigurationRule[];
  cameraRef: React.RefObject<View3DCamera>;
  annotations: Annotation3D[];
}>({
  configurationRules: [],
  cameraRef,
  annotations: [],
});

export default function AppView3DContextProvider(props: React.PropsWithChildren) {
  const appState = useAppState();
  const pageContent = getPageContent(appState.orderView, appState.activePage);
  const configurationRules = getConfigurationRulesForPage(appState.activePage, appState.orderView, 'all');

  let annotations: Annotation3D[] = [];
  if (isContentModel(pageContent) && isPageReferenceActivity(appState.activePage)) {
    const activityContent = pageContent as ActivityView;
    annotations = activityContent.annotations.map(
      (annotation): Annotation3D => ({
        ...annotation,
        color: annotationColors[0],
        ref3DObject: annotation.CADSubjects.map(() => ({ center: new Vector3() })),
        annotationRef: React.createRef(),
      }),
    );
  }

  return <context.Provider value={{ configurationRules, cameraRef, annotations }}>{props.children}</context.Provider>;
}

export function useView3DCamera(): View3DCamera {
  const { cameraRef } = React.useContext(context);

  return {
    goHome: () => {
      if (cameraRef.current) {
        cameraRef.current.goHome();
      } else {
        throw new Error('No camera ref');
      }
    },
    zoomFit: () => {
      if (cameraRef.current) {
        cameraRef.current.zoomFit();
      } else {
        throw new Error('No camera ref');
      }
    },
    getState() {
      if (cameraRef.current) {
        return cameraRef.current.getState();
      } else {
        throw new Error('No camera ref');
      }
    },
    get2DCoordinates(position) {
      if (cameraRef.current) {
        return cameraRef.current.get2DCoordinates(position);
      } else {
        throw new Error('No camera ref');
      }
    },
  };
}
export function useView3DCameraRef(): React.RefObject<View3DCamera> {
  return React.useContext(context).cameraRef;
}
export function useView3DAnnotations(): Annotation3D[] {
  return React.useContext(context).annotations;
}

export function useView3DRepState(reference: string[]): View3DRepState {
  const { configurationRules, annotations } = React.useContext(context);

  const reducedConfig = applyConfigurationRules(reference, configurationRules);

  const annotation = annotations.find((ann) => ann.CADSubjects.some((sub) => refIsEqual(reference, sub.ref)));
  const subjectIndex = annotation ? annotation.CADSubjects.findIndex((k) => refIsEqual(reference, k.ref)) : 0;
  const CADSubject = annotation ? annotation.CADSubjects[subjectIndex] : undefined;
  const ref3DObject = annotation ? annotation.ref3DObject[subjectIndex] : undefined;

  return {
    visible: reducedConfig.visible,
    actionState: annotation ? (CADSubject?.offsetPosition ? 'animated' : 'active') : 'none',
    offsetPosition: CADSubject && CADSubject.offsetPosition ? CADSubject.offsetPosition : [0, 0, 0],
    color: annotation ? annotation.color : 0,
    ref3DObject,
  };
}
